import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, IconButton, Tooltip } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { useTranslation } from "../../../context/Internationalization";
import { downloadFileMedia, getFileMedia, isLocalFile } from "./fileMedia";
import FilePreview from "./FilePreview";

export default function FileActions({
  file,
  disabled = false,
  previewEnabled = true,
  children,
}) {
  const { t } = useTranslation();
  const { url, name, type, previewKind, available } = getFileMedia(file);
  const identity = isLocalFile(file) ? file : JSON.stringify([url, name, type]);
  const [preview, setPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);
  const request = useRef(0);
  const inFlight = useRef(false);

  useEffect(() => {
    setPreview(false);
    setError(false);
    setDownloading(false);
    inFlight.current = false;
    return () => {
      request.current += 1;
    };
  }, [identity]);

  const download = async (event) => {
    event.stopPropagation();
    if (inFlight.current) return;
    inFlight.current = true;
    const current = request.current;
    setDownloading(true);
    setError(false);
    try {
      await downloadFileMedia(file);
    } catch (_) {
      if (request.current === current) setError(true);
    } finally {
      if (request.current === current) {
        inFlight.current = false;
        setDownloading(false);
      }
    }
  };

  const downloadError = error && (
    <Alert
      severity="error"
      sx={{
        width: "100%",
        boxSizing: "border-box",
        alignItems: "flex-start",
        "& .MuiAlert-message": { minWidth: 0, width: "100%" },
      }}
    >
      <Box sx={{ overflowWrap: "anywhere" }}>
        {t("file_download_failed_help")}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
        <Button
          type="button"
          size="small"
          variant="outlined"
          onClick={download}
          disabled={disabled || downloading}
        >
          {t("retry")}
        </Button>
        {/^(https?:|blob:)/i.test(url) && (
          <Button
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
          >
            {t("open_file")}
          </Button>
        )}
      </Box>
    </Alert>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
      }}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {available && previewKind && previewEnabled && (
        <Tooltip title={t("preview_file")}>
          <span>
            <IconButton
              type="button"
              size="small"
              disabled={disabled}
              aria-label={`${t("preview_file")} ${name}`}
              onClick={() => setPreview(true)}
            >
              <VisibilityOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {available && (
        <Tooltip title={t("download_file")}>
          <span>
            <IconButton
              type="button"
              size="small"
              disabled={disabled || downloading}
              aria-label={`${t("download_file")} ${name}`}
              onClick={download}
            >
              <DownloadOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {children}
      {!preview && downloadError}
      {preview && (
        <FilePreview
          file={file}
          onClose={() => setPreview(false)}
          actions={
            error ? (
              downloadError
            ) : (
              <Button
                type="button"
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                onClick={download}
                disabled={downloading || disabled}
              >
                {t("download_file")}
              </Button>
            )
          }
        />
      )}
    </Box>
  );
}
